import CachedRoundedIcon from "@mui/icons-material/CachedRounded";
import ModalActionButtons from "components/shared/ModalActionButtons";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { formatLocaleDateString } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refreshSalesAnalytics } from "store/analyticsStore";
import { fetchAnalyticsJobs } from "store/jobsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import type { AnalyticsRefreshContext } from "../Dashboard";

/**
 * Recomputes the company's analytics data, shared by the Dashboard and Consolidated sales tabs (both read it).
 * Asks for confirmation first: the recomputation can take several minutes on large sales volumes.
 * It runs in the background: the page follows its job (see useAnalyticsRefreshJob) and reloads when it ends.
 */
const AnalyticsRefreshButton = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { getCompanySettings, companyInfos } = useCompanySettings();
    const { analyticsSalesRefreshStatus } = useAppSelector((state) => state.analytics);
    const { analyticsJobs } = useAppSelector((state) => state.jobs);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const refreshContext: AnalyticsRefreshContext = useMemo(
        () => getCompanySettings("analytics_refresh_context"),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [companyInfos],
    );

    const isRefreshing = analyticsSalesRefreshStatus === "pending";

    const tooltip = useMemo(() => {
        if (isRefreshing) {
            return t("Analytics.Refreshing");
        }
        if (analyticsJobs.computing) {
            return t("Analytics.Computing");
        }
        if (refreshContext?.needsRecompute && refreshContext?.date) {
            const date = formatLocaleDateString({
                date: refreshContext.date,
                options: { numeric: true, showTime: true, timeZone: "UTC" },
            });
            return (
                <div className="flex flex-col">
                    <span>{t("Analytics.LastModified")}</span>
                    <span>{`${t(`Analytics.RefreshReason_${refreshContext.reason}`)} - ${date}`}</span>
                </div>
            );
        }
        return "";
    }, [isRefreshing, analyticsJobs.computing, refreshContext, t]);

    const launchRefresh = () => {
        setIsConfirmOpen(false);
        dispatch(refreshSalesAnalytics()).then((action) => {
            // Started: fetch its job now so the page switches to the progress at once, not at the next poll
            if (refreshSalesAnalytics.fulfilled.match(action)) dispatch(fetchAnalyticsJobs());
        });
    };

    return (
        <>
            <StyledButton
                buttonType={refreshContext?.needsRecompute ? "outlined" : "secondary"}
                tooltipText={tooltip}
                onClick={() => setIsConfirmOpen(true)}
                disabled={isRefreshing || analyticsJobs.computing}
            >
                <CachedRoundedIcon sx={{ width: "16px", height: "16px" }} />
                {t("Analytics.Refresh")}
            </StyledButton>
            <ModalBase
                open={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                modalTitle={t("Analytics.RefreshModal.Title")}
            >
                <div className="flex flex-col gap-6">
                    <p>{t("Analytics.RefreshModal.Description")}</p>
                    <ModalActionButtons
                        buttonsDetails={{
                            cancel: { label: t("ModalBase.Cancel"), action: () => setIsConfirmOpen(false) },
                            confirm: { label: t("Analytics.RefreshModal.Confirm"), action: launchRefresh },
                        }}
                    />
                </div>
            </ModalBase>
        </>
    );
};

export default AnalyticsRefreshButton;
